import { AxiosResponse } from 'axios';
import { mapFxSubscription } from '@/lib/mappers';
import { filterByIdOrShortId } from '@/lib/shortId';
import { supabase } from '@/lib/supabase';
import {
  asResponse,
  paginationRange,
  buildPaginatedResponse,
  throwIfError,
} from '@/lib/supabase-helpers';
import { getActiveOrgId, applyOrgFilter } from '@/lib/activeOrg';
import { ResponseWithPagination } from '@/types';
import { FxSubscription, FxSubscriptionStatus } from '@/types/api/finance';

const SELECT_WITH_VENDOR = '*, fx_vendors(*, fx_categories(*))';

const financeSubscriptionsService = {
  async getAll({
    page,
    take,
    status,
  }: {
    page: number;
    take: number;
    status?: FxSubscriptionStatus;
  }): Promise<AxiosResponse<ResponseWithPagination<FxSubscription[]>>> {
    const [from, to] = paginationRange(page, take);
    let query = supabase
      .from('fx_subscriptions')
      .select(SELECT_WITH_VENDOR, { count: 'exact' })
      .order('name', { ascending: true })
      .range(from, to);

    query = applyOrgFilter(query);

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return asResponse(
      buildPaginatedResponse((data ?? []).map(mapFxSubscription), count ?? 0, page, take)
    );
  },

  async getById(id: string): Promise<AxiosResponse<FxSubscription>> {
    let query = supabase
      .from('fx_subscriptions')
      .select(SELECT_WITH_VENDOR);
    query = filterByIdOrShortId(query, id);

    query = applyOrgFilter(query);

    const { data, error } = await query.single();

    if (error) throw error;
    return asResponse(mapFxSubscription(data));
  },

  async create(payload: {
    vendorId: string;
    name: string;
    amount: number;
    currency?: string;
    billingCycle: string;
    startDate: string;
    endDate?: string;
    nextBilling?: string;
    autoRenew?: boolean;
    seats?: number;
    status?: string;
    owner?: string;
    notes?: string;
  }): Promise<AxiosResponse<FxSubscription>> {
    const orgId = getActiveOrgId();
    const insert: Record<string, unknown> = {
      vendor_id: payload.vendorId,
      name: payload.name,
      amount: payload.amount,
      billing_cycle: payload.billingCycle,
      start_date: payload.startDate,
      ...(orgId ? { organization_id: orgId } : {}),
    };
    if (payload.currency !== undefined) insert.currency = payload.currency;
    if (payload.endDate !== undefined) insert.end_date = payload.endDate;
    if (payload.nextBilling !== undefined) insert.next_billing = payload.nextBilling;
    if (payload.autoRenew !== undefined) insert.auto_renew = payload.autoRenew;
    if (payload.seats !== undefined) insert.seats = payload.seats;
    if (payload.status !== undefined) insert.status = payload.status;
    if (payload.owner !== undefined) insert.owner = payload.owner;
    if (payload.notes !== undefined) insert.notes = payload.notes;

    const result = await supabase
      .from('fx_subscriptions')
      .insert(insert)
      .select(SELECT_WITH_VENDOR)
      .single();

    return asResponse(mapFxSubscription(throwIfError(result)));
  },

  async update(
    id: string,
    payload: {
      vendorId?: string;
      name?: string;
      amount?: number;
      currency?: string;
      billingCycle?: string;
      startDate?: string;
      endDate?: string | null;
      nextBilling?: string | null;
      autoRenew?: boolean;
      seats?: number | null;
      status?: string;
      owner?: string;
      notes?: string;
    }
  ): Promise<AxiosResponse<FxSubscription>> {
    const updates: Record<string, unknown> = {};
    if (payload.vendorId !== undefined) updates.vendor_id = payload.vendorId;
    if (payload.name !== undefined) updates.name = payload.name;
    if (payload.amount !== undefined) updates.amount = payload.amount;
    if (payload.currency !== undefined) updates.currency = payload.currency;
    if (payload.billingCycle !== undefined) updates.billing_cycle = payload.billingCycle;
    if (payload.startDate !== undefined) updates.start_date = payload.startDate;
    if (payload.endDate !== undefined) updates.end_date = payload.endDate;
    if (payload.nextBilling !== undefined) updates.next_billing = payload.nextBilling;
    if (payload.autoRenew !== undefined) updates.auto_renew = payload.autoRenew;
    if (payload.seats !== undefined) updates.seats = payload.seats;
    if (payload.status !== undefined) updates.status = payload.status;
    if (payload.owner !== undefined) updates.owner = payload.owner;
    if (payload.notes !== undefined) updates.notes = payload.notes;

    const result = await supabase
      .from('fx_subscriptions')
      .update(updates)
      .eq('id', id)
      .select(SELECT_WITH_VENDOR)
      .single();

    return asResponse(mapFxSubscription(throwIfError(result)));
  },

  async cancel(id: string): Promise<AxiosResponse<FxSubscription>> {
    const result = await supabase
      .from('fx_subscriptions')
      .update({ status: 'cancelled' })
      .eq('id', id)
      .select(SELECT_WITH_VENDOR)
      .single();

    return asResponse(mapFxSubscription(throwIfError(result)));
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('fx_subscriptions').delete().eq('id', id);

    if (error) throw error;
  },

  async getActiveByVendorIds(vendorIds: string[]): Promise<AxiosResponse<FxSubscription[]>> {
    if (vendorIds.length === 0) return asResponse([]);

    let query = supabase
      .from('fx_subscriptions')
      .select(SELECT_WITH_VENDOR)
      .in('vendor_id', vendorIds)
      .in('status', ['active', 'trial']);

    query = applyOrgFilter(query);

    const { data, error } = await query;

    if (error) throw error;
    return asResponse((data ?? []).map(mapFxSubscription));
  },

  async getUpcomingRenewals(days = 30): Promise<AxiosResponse<FxSubscription[]>> {
    const today = new Date().toISOString().split('T')[0];
    const futureDate = new Date(Date.now() + days * 86400000).toISOString().split('T')[0];

    let query = supabase
      .from('fx_subscriptions')
      .select(SELECT_WITH_VENDOR)
      .eq('status', 'active')
      .gte('next_billing', today)
      .lte('next_billing', futureDate)
      .order('next_billing', { ascending: true });

    query = applyOrgFilter(query);

    const { data, error } = await query;

    if (error) throw error;
    return asResponse((data ?? []).map(mapFxSubscription));
  },
};

export default financeSubscriptionsService;
